import { useState, useEffect } from 'react';
import StockTable from './StockTable';
import { Stock } from '../data/mockData';
import { useData } from '../context/DataContext';

export default function StockMagic() {
  const [activeTab, setActiveTab] = useState<'profit' | 'stage2'>('profit');
  
  const { stocks, signals, isLoading } = useData();
  const [profitGapStocks, setProfitGapStocks] = useState<Stock[]>([]);
  const [stage2Stocks, setStage2Stocks] = useState<Stock[]>([]);

  useEffect(() => {
    if (stocks.length > 0) {
      // Filter Profit Gap (净利润断层)
      const profitGapCodes = new Set(signals
        .filter(s => s.signalType === '净利润断层' || s.signalType === 'profit_gap')
        .map(s => s.stock.code));
      setProfitGapStocks(stocks.filter(s => profitGapCodes.has(s.code)));

      // Filter Stage 2 / Three Line Red (三线翻红)
      const stage2Codes = new Set(signals
        .filter(s => s.signalType === '三线翻红' || s.signalType === 'three_line_red' || s.signalType === 'stage2')
        .map(s => s.stock.code));
      setStage2Stocks(stocks.filter(s => stage2Codes.has(s.code)));
    }
  }, [stocks, signals]);

  const tabs = [
    { id: 'profit' as const, label: '净利润断层' },
    { id: 'stage2' as const, label: '三线翻红' }
  ];

  const columns = [
    {
      key: 'code' as const,
      label: '代码'
    },
    {
      key: 'name' as const,
      label: '名称',
      render: (_value: string, stock: Stock) => (
        <div className="flex items-center gap-1.5">
          <span>{stock.name}</span>
          {stock.topic && (
            <span className="text-xs px-1.5 py-0.5 bg-[#1a56db]/10 text-[#1a56db] rounded">
              {stock.topic}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'changePercent' as const,
      label: '涨跌幅',
      render: (value: number) => (
        <span className={value >= 0 ? 'text-[#dc3545]' : 'text-[#28a745]'}>
          {value >= 0 ? '+' : ''}{value?.toFixed(2)}%
        </span>
      )
    },
    {
      key: 'signalDate' as const,
      label: '信号日',
      render: (value: string) => <span className="text-muted-foreground">{value || '-'}</span>
    },
    {
      key: 'dataDate' as const,
      label: '数据日',
      render: (value: string) => <span className="text-muted-foreground">{value || '-'}</span>
    }
  ];

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin w-6 h-6 border-2 border-[#1a56db] border-t-transparent rounded-full" />
          <span>加载数据中...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <h1 className="text-2xl font-semibold mb-4 text-foreground">股魔</h1>
        
        {/* Tabs */}
        <div className="flex border-b border-border mb-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 pb-3 text-sm font-medium transition-colors relative ${
                activeTab === tab.id 
                  ? 'text-[#1a56db]' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1a56db]" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden bg-background">
        {activeTab === 'profit' && (
          <StockTable 
            stocks={profitGapStocks} 
            columns={columns} 
            defaultSort={{ key: 'changePercent', direction: 'desc' }}
            pageSize={20}
          />
        )}
        
        {activeTab === 'stage2' && (
          <StockTable 
            stocks={stage2Stocks} 
            columns={columns} 
            defaultSort={{ key: 'changePercent', direction: 'desc' }}
            pageSize={20}
          />
        )}
      </div>
    </div>
  );
}